// IE Hub - Engine Provider
// Provides JSON screen engine context and functionality

import React, { createContext, useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// import { runEffects } from '../../engine/actions/effects.js'; // Unused - keeping for future use
import { applyComputeMap } from '../../engine/runtime/compute.js';
import { FormService } from '../../shared/api/services/formService';
import type { Schema } from '../../shared/api/types';

// Schema types - using imported Schema type from API types

interface EngineContextType {
  schemas: Map<string, Schema>;
  loadSchema: (schemaId: string) => Promise<Schema | null>;
  submitForm: (schemaId: string, formData: any, options?: any) => Promise<any>;
  validateSchema: (schema: Schema) => Promise<boolean>;
  clearCache: () => void;
  isLoading: boolean;
  error: string | null;
}

interface EngineProviderProps {
  children: React.ReactNode;
}

// Engine Context
const EngineContext = createContext<EngineContextType>({
  schemas: new Map(),
  loadSchema: () => Promise.resolve(null),
  submitForm: () => Promise.resolve(),
  validateSchema: () => Promise.resolve(true),
  clearCache: () => {},
  isLoading: false,
  error: null
});

export const useEngine = (): EngineContextType => {
  const context = useContext(EngineContext);
  if (!context) {
    throw new Error('useEngine must be used within an EngineProvider');
  }
  return context;
};

function EngineProvider({ children }: EngineProviderProps) {
  const navigate = useNavigate();
  // State
  const [schemas, setSchemas] = useState<Map<string, Schema>>(new Map());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Validate schema structure
  const validateSchema = useCallback(async (schema: Schema): Promise<boolean> => {
    try {
      // Basic structure validation
      if (!schema || typeof schema !== 'object') {
        return false;
      }

      // Check for either old or new schema format
      const hasOldFormat = 'id' in schema && 'layout' in schema;
      const hasNewFormat = 'formName' in schema && 'sections' in schema;

      if (!hasOldFormat && !hasNewFormat) {
        console.error('Schema must have either old format (id, layout) or new format (formName, sections)');
        return false;
      }

      // Validate version
      if ('version' in schema) {
        // For new format, version can be a number
        if (hasNewFormat && typeof schema.version === 'number') {
          // Valid number version
        } else if (typeof schema.version === 'string' && schema.version.match(/^\d+(\.\d+\.\d+)?$/)) {
          // Valid string version (semantic or simple)
        } else {
          console.error(`Invalid version format: ${schema.version}`);
          return false;
        }
      }

      // Validate structure based on format
      if (hasNewFormat) {
        // New format validation
        if (!Array.isArray(schema.sections)) {
          console.error('Sections must be an array');
          return false;
        }

        // Validate each section has required fields
        for (const section of schema.sections) {
          if (!section.sectionName || !Array.isArray(section.fields)) {
            console.error('Each section must have sectionName and fields array');
            return false;
          }
        }
      } else {
        // Old format validation
        if (!schema.layout || typeof schema.layout !== 'object') {
          console.error('Invalid layout structure');
          return false;
        }

        if (schema.layout.fields && !Array.isArray(schema.layout.fields)) {
          console.error('Fields must be an array');
          return false;
        }
      }

      return true;
    } catch (err) {
      console.error('Schema validation error:', err);
      return false;
    }
  }, []);

  // Load schema from API or cache using FormService
  const loadSchema = useCallback(async (schemaId: string): Promise<Schema | null> => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Check cache first
      if (schemas.has(schemaId)) {
        return schemas.get(schemaId) || null;
      }
      
      // Use FormService to get schema
      const response = await FormService.getSchema(schemaId);
      
      if (!response.success || !response.data) {
        throw new Error(response.message || `Schema ${schemaId} not found`);
      }
      
      const schema = response.data;
      
      // Validate schema structure
      const isValid = await validateSchema(schema);
      if (!isValid) {
        throw new Error('Invalid schema structure');
      }
      
      // Apply compute.beforeRender if present
      if (schema.compute?.beforeRender) {
        const computed = applyComputeMap(schema.compute.beforeRender, {});
        schema.initialComputed = computed;
      }
      
      // Cache the schema
      setSchemas(prev => new Map(prev).set(schemaId, schema));
      
      return schema;
      
    } catch (err) {
      console.error('Error loading schema:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [schemas, validateSchema]);

  // Submit form data using FormService
  const submitForm = useCallback(async (schemaId: string, formData: any, options: any = {}): Promise<any> => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Use FormService to submit form
      const response = await FormService.submitForm(schemaId, formData, {
        navigate,
        metadata: options.metadata,
        config: {
          timeout: options.timeout,
          headers: options.headers
        }
      });
      
      if (!response.success) {
        throw new Error(response.message || 'Form submission failed');
      }
      
      return response.data;
      
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  // No deprecated executeActions function needed

  // Clear schema cache
  const clearCache = useCallback(() => {
    setSchemas(new Map());
    FormService.clearCache(); // Also clear FormService cache
  }, []);
  
  // Context value
  const contextValue: EngineContextType = {
    schemas,
    loadSchema,
    submitForm,
    validateSchema,
    clearCache,
    isLoading,
    error
  };
  
  return (
    <EngineContext.Provider value={contextValue}>
      {children}
    </EngineContext.Provider>
  );
}

export default EngineProvider;
